import { styled } from "goober";
import type React from "react";
import { useEffect, useRef } from "react";
import { Icon, type IconName } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";

export type ColumnItem = {
  id: string;
  label: string;
  meta?: Record<string, string>;
  /** Standard icon name. Ignored if `left` is provided. */
  icon?: IconName;
  iconColor?: string;
  /** Custom left element (e.g. workflow badge, step color bar). Overrides `icon`. */
  left?: React.ReactNode;
  right?: React.ReactNode;
  drillable?: boolean;
};

type Props = {
  heading?: string;
  items: ColumnItem[];
  selectedId: string | null;
  focusedColumn: boolean;
  focusedIndex: number;
  columnIndex: number;
  onSelect: (
    id: string,
    label: string,
    rowIndex: number,
    meta?: Record<string, string>
  ) => void;
  onReplace?: (
    id: string,
    label: string,
    meta?: Record<string, string>
  ) => void;
  onRegisterCount: (columnIndex: number, count: number) => void;
  onContextMenu?: (id: string, rowIndex: number, x: number, y: number) => void;
};

const Column = styled("div")`
  width: 228px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-right: 0.5px solid var(--border-alpha);
  height: 100%;
`;

const Heading = styled("div")`
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: var(--text-secondary);
  position: sticky;
  top: 0;
  background: var(--surface-primary);
  border-bottom: 0.5px solid var(--border-alpha);
  z-index: 1;
`;

const ItemList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
`;

const ItemRow = styled(PressButton)`
  padding: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  color: var(--text-primary);
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;

  && {
    border-radius: 8px;
  }

  &:hover {
    background-color: var(--alpha4);
  }

  &[data-selected="true"] {
    background-color: var(--alpha4);
  }

  &[data-highlighted="true"] {
    background-color: var(--control-bg);
    color: var(--control-fg);
  }
`;

const Spacer = styled("div")`
  flex: 1;
  min-width: 0;
`;

export function FileBrowserColumn({
  heading,
  items,
  selectedId,
  focusedColumn,
  focusedIndex,
  columnIndex,
  onSelect,
  onReplace,
  onRegisterCount,
  onContextMenu,
}: Props) {
  const columnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onRegisterCount(columnIndex, items.length);
  }, [columnIndex, items.length, onRegisterCount]);

  // Whenever the focused item changes, auto-select it so the child column
  // shows immediately — no Enter/ArrowRight needed.
  useEffect(() => {
    if (!focusedColumn || !onReplace || focusedIndex < 0) return;
    const item = items[focusedIndex];
    if (!item) return;
    if (item.id === selectedId) return;
    onReplace(item.id, item.label, item.meta);
  }, [focusedColumn, focusedIndex, items, onReplace, selectedId]);

  // Scroll focused item into view
  useEffect(() => {
    if (!focusedColumn || focusedIndex < 0) return;
    const el = columnRef.current;
    if (!el) return;
    const rows = el.querySelectorAll("[data-row]");
    rows[focusedIndex]?.scrollIntoView({ block: "nearest" });
  }, [focusedColumn, focusedIndex]);

  return (
    <Column ref={columnRef}>
      {heading && (
        <Heading>
          <Text variant="text-small" color="var(--text-secondary)">
            {heading}
          </Text>
        </Heading>
      )}
      <ItemList>
        {items.map((item, i) => (
          <ItemRow
            key={item.id}
            data-row
            data-selected={item.id === selectedId}
            data-highlighted={focusedColumn && focusedIndex === i}
            onPress={() => onSelect(item.id, item.label, i, item.meta)}
            onContextMenu={
              onContextMenu
                ? (e) => {
                    e.preventDefault();
                    onContextMenu(item.id, i, e.clientX, e.clientY);
                  }
                : undefined
            }
          >
            {item.left ?? (
              <Icon
                name={item.icon ?? "hash"}
                size={16}
                color={item.iconColor ?? "currentColor"}
                style={{ flexShrink: 0 }}
              />
            )}
            <Text ellipsis>{item.label}</Text>
            <Spacer />
            {item.right}
            {item.drillable !== false && (
              <Icon
                name="chevron-right"
                size={12}
                color="currentColor"
                style={{ opacity: 0.2 }}
              />
            )}
          </ItemRow>
        ))}
      </ItemList>
    </Column>
  );
}
